import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Edit, Trash2, Save, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RichTextEditor } from "@/components/course/RichTextEditor";

interface Tutorial {
  id: string;
  title: string;
  description: string;
  duration_minutes: number;
  difficulty: string;
  category: string;
  is_active: boolean;
  created_at: string;
  created_by?: string;
}

interface TutorialStep {
  id: string;
  tutorial_id: string;
  title: string;
  description: string;
  content: any;
  step_order: number;
  is_active: boolean;
}

interface TutorialFormData {
  title: string;
  description: string;
  duration_minutes: number;
  difficulty: string;
  category: string;
  is_active: boolean;
}

const initialFormData: TutorialFormData = {
  title: '',
  description: '',
  duration_minutes: 30,
  difficulty: 'beginner',
  category: 'Foundation',
  is_active: true,
};

export function TutorialManagement() {
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [selectedTutorial, setSelectedTutorial] = useState<Tutorial | null>(null);
  const [tutorialSteps, setTutorialSteps] = useState<TutorialStep[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isStepDialogOpen, setIsStepDialogOpen] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState<Tutorial | null>(null);
  const [editingStep, setEditingStep] = useState<TutorialStep | null>(null);
  const [formData, setFormData] = useState<TutorialFormData>(initialFormData);
  const [stepFormData, setStepFormData] = useState({
    title: '',
    description: '',
    content: '',
    step_order: 1,
    is_active: true,
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadTutorials();
  }, []);

  useEffect(() => {
    if (selectedTutorial) {
      loadTutorialSteps(selectedTutorial.id);
    }
  }, [selectedTutorial]);

  const loadTutorials = async () => {
    // Use hardcoded tutorials for now since the database table may not exist yet
    const mockTutorials: Tutorial[] = [
      {
        id: 'getting-started',
        title: 'Getting Started with Course Creation',
        description: 'Learn the basics of creating effective professional development courses',
        duration_minutes: 30,
        difficulty: 'beginner',
        category: 'Foundation',
        is_active: true,
        created_at: new Date().toISOString(),
        created_by: 'system'
      },
      {
        id: 'content-development',
        title: 'Content Development Best Practices',
        description: 'Master the art of creating engaging and effective course content',
        duration_minutes: 45,
        difficulty: 'intermediate', 
        category: 'Content',
        is_active: true,
        created_at: new Date().toISOString(),
        created_by: 'system'
      },
      {
        id: 'online-delivery',
        title: 'Online Course Delivery',
        description: 'Master the technical and pedagogical aspects of online course delivery',
        duration_minutes: 40,
        difficulty: 'intermediate',
        category: 'Technology',
        is_active: true,
        created_at: new Date().toISOString(),
        created_by: 'system'
      }
    ];

    setLoading(false);
    setTutorials(mockTutorials);
  };

  const loadTutorialSteps = async (tutorialId: string) => {
    // Use mock data for now
    const mockSteps: TutorialStep[] = [
      {
        id: `step-1-${tutorialId}`,
        tutorial_id: tutorialId,
        title: 'Introduction',
        description: 'Get started with this tutorial',
        content: '<p>Welcome to this tutorial section!</p>',
        step_order: 1,
        is_active: true
      },
      {
        id: `step-2-${tutorialId}`,
        tutorial_id: tutorialId,
        title: 'Main Content',
        description: 'Learn the core concepts',
        content: '<p>Here is the main content of the tutorial.</p>',
        step_order: 2,
        is_active: true
      }
    ];
    
    setTutorialSteps(mockSteps);
  };

  const handleSaveTutorial = async () => {
    // For now, just show success - database integration can be added later
    toast({ 
      title: "Success", 
      description: editingTutorial ? "Tutorial updated successfully" : "Tutorial created successfully"
    });

    setIsDialogOpen(false);
    setEditingTutorial(null);
    setFormData(initialFormData);
    loadTutorials();
  };

  const handleSaveStep = async () => {
    // For now, just show success - database integration can be added later
    toast({ 
      title: "Success", 
      description: editingStep ? "Step updated successfully" : "Step created successfully"
    });

    setIsStepDialogOpen(false);
    setEditingStep(null);
    setStepFormData({
      title: '',
      description: '',
      content: '',
      step_order: tutorialSteps.length + 1,
      is_active: true,
    });
    if (selectedTutorial) {
      loadTutorialSteps(selectedTutorial.id);
    }
  };

  const handleDeleteTutorial = async (tutorialId: string) => {
    if (!confirm('Are you sure you want to delete this tutorial? This will also delete all associated steps.')) {
      return;
    }

    // For now, just show success - database integration can be added later
    toast({ title: "Success", description: "Tutorial deleted successfully" });
    loadTutorials();
    if (selectedTutorial?.id === tutorialId) {
      setSelectedTutorial(null);
      setTutorialSteps([]);
    }
  };

  const handleDeleteStep = async (stepId: string) => {
    if (!confirm('Are you sure you want to delete this step?')) return;

    // For now, just show success - database integration can be added later
    toast({ title: "Success", description: "Step deleted successfully" });
    if (selectedTutorial) {
      loadTutorialSteps(selectedTutorial.id);
    }
  };

  const openEditDialog = (tutorial?: Tutorial) => {
    if (tutorial) {
      setEditingTutorial(tutorial);
      setFormData({
        title: tutorial.title,
        description: tutorial.description,
        duration_minutes: tutorial.duration_minutes,
        difficulty: tutorial.difficulty,
        category: tutorial.category,
        is_active: tutorial.is_active,
      });
    } else {
      setEditingTutorial(null);
      setFormData(initialFormData);
    }
    setIsDialogOpen(true);
  };

  const openStepDialog = (step?: TutorialStep) => {
    if (step) {
      setEditingStep(step);
      setStepFormData({
        title: step.title,
        description: step.description,
        content: typeof step.content === 'string' ? step.content : JSON.stringify(step.content),
        step_order: step.step_order,
        is_active: step.is_active,
      });
    } else {
      setEditingStep(null);
      setStepFormData({
        title: '',
        description: '',
        content: '',
        step_order: tutorialSteps.length + 1,
        is_active: true,
      });
    }
    setIsStepDialogOpen(true);
  };

  if (loading) {
    return <div className="p-6">Loading tutorials...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Tutorial Management</h1>
          <p className="text-muted-foreground">Create and manage course creation tutorials</p>
        </div>
        <Button onClick={() => openEditDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          New Tutorial
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tutorials List */}
        <Card>
          <CardHeader>
            <CardTitle>Tutorials</CardTitle>
            <CardDescription>
              Manage your tutorial library
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {tutorials.map((tutorial) => (
                <div
                  key={tutorial.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedTutorial?.id === tutorial.id 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:bg-muted/50'
                  }`}
                  onClick={() => setSelectedTutorial(tutorial)}
                >
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <h3 className="font-semibold">{tutorial.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {tutorial.description}
                      </p>
                      <div className="flex items-center gap-2">
                        <Badge variant={tutorial.is_active ? "default" : "secondary"}>
                          {tutorial.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <Badge variant="outline">{tutorial.difficulty}</Badge>
                        <Badge variant="outline">{tutorial.category}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(tutorial);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTutorial(tutorial.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tutorial Steps */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>
                  {selectedTutorial ? `${selectedTutorial.title} - Steps` : 'Tutorial Steps'}
                </CardTitle>
                <CardDescription>
                  {selectedTutorial ? 'Manage the steps for this tutorial' : 'Select a tutorial to manage its steps'}
                </CardDescription>
              </div>
              {selectedTutorial && (
                <Button onClick={() => openStepDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Step
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedTutorial ? (
              <div className="space-y-4">
                {tutorialSteps.map((step, index) => (
                  <div key={step.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <h4 className="font-medium">Step {step.step_order}: {step.title}</h4>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {step.description}
                        </p>
                        <Badge variant={step.is_active ? "default" : "secondary"}>
                          {step.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openStepDialog(step)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteStep(step.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {tutorialSteps.length === 0 && (
                  <div className="text-center text-muted-foreground py-8">
                    No steps found. Add your first step to get started.
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                Select a tutorial to view and manage its steps
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tutorial Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTutorial ? 'Edit Tutorial' : 'Create New Tutorial'}
            </DialogTitle>
            <DialogDescription>
              {editingTutorial ? 'Update the tutorial details' : 'Create a new tutorial for the course creation academy'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Tutorial title"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Tutorial description"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
                  placeholder="30"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="difficulty">Difficulty</Label>
                <Select
                  value={formData.difficulty}
                  onValueChange={(value) => setFormData({ ...formData, difficulty: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Foundation">Foundation</SelectItem>
                  <SelectItem value="Content">Content</SelectItem>
                  <SelectItem value="Technology">Technology</SelectItem>
                  <SelectItem value="Quality">Quality</SelectItem>
                  <SelectItem value="Assessment">Assessment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTutorial}>
              <Save className="h-4 w-4 mr-2" />
              Save Tutorial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step Dialog */}
      <Dialog open={isStepDialogOpen} onOpenChange={setIsStepDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStep ? 'Edit Step' : 'Create New Step'}
            </DialogTitle>
            <DialogDescription>
              {editingStep ? 'Update the step details' : 'Add a new step to the tutorial'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="step-title">Title</Label>
              <Input
                id="step-title"
                value={stepFormData.title}
                onChange={(e) => setStepFormData({ ...stepFormData, title: e.target.value })}
                placeholder="Step title"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="step-description">Description</Label>
              <Textarea
                id="step-description"
                value={stepFormData.description}
                onChange={(e) => setStepFormData({ ...stepFormData, description: e.target.value })}
                placeholder="Step description"
                rows={2}
              />
            </div>
            
            <div className="grid gap-2">
              <Label>Content</Label>
              <div className="border rounded-lg p-2">
                <Textarea
                  value={stepFormData.content}
                  onChange={(e) => setStepFormData({ ...stepFormData, content: e.target.value })}
                  placeholder="Step content (HTML supported)"
                  rows={8}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="step-order">Step Order</Label>
                <Input
                  id="step-order"
                  type="number"
                  value={stepFormData.step_order}
                  onChange={(e) => setStepFormData({ ...stepFormData, step_order: parseInt(e.target.value) || 1 })}
                  min={1}
                />
              </div>
              
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="step-active"
                  checked={stepFormData.is_active}
                  onCheckedChange={(checked) => setStepFormData({ ...stepFormData, is_active: checked })}
                />
                <Label htmlFor="step-active">Active</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStepDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveStep}>
              <Save className="h-4 w-4 mr-2" />
              Save Step
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}